<template>
  <ValidationProvider
    tag="div"
    :rules="getRules"
    :name="label"
    :vid="label + '-' + $id()"
    :class="{
      'f-col-span-2': true,
      'sm:f-col-span-1': !singleColumn,
    }"
    v-show="show"
    v-slot="{ errors }"
  >
    <BaseInputGroup
      :for="id"
      :label="label + (required ? '*' : '')"
      :error="getFirstError(errors)"
      :single-column="singleColumn"
      :class="{
        'f-col-span-2': true,
        'sm:f-col-span-1': !singleColumn,
      }"
      class="f-relative"
    >
      <template slot="input">
        <div
          :class="
            singleColumn
              ? 'f-flex f-w-full f-top-0 sm:f-top-auto sm:f-relative f-text-gray-700'
              : 'f-flex f-w-full f-text-gray-700'
          "
          v-click-outside="closeDropdown"
        >
          <div class="f-z-10">
            <button
              type="button"
              tabindex="0"
              class="f-relative f-bg-transparent f-border-r focus:f-border-r f-mt-px f-border-gray-300 f-rounded-l-md f-shadow-sm f-px-3 f-pr-6 f-py-1 f-text-left f-cursor-default focus:f-outline-none focus:f-ring-2 focus:f-ring-indigo-500 focus:f-border-0"
              @mouseenter="triggerLoadPhones"
              @click="triggerShowPhone"
            >
              <span class="f-flex f-items-center">
                <img
                  :src="getFlagUrl(phoneCountry)"
                  :alt="callingCode"
                  class="f-flex-shrink-0 f-w-5"
                />
                <span class="f-ml-2 f-block f-truncate f-text-gray-700">
                  +{{ callingCode }}
                </span>
              </span>
              <span
                class="f-absolute f-inset-y-0 f-right-0 f-flex f-items-center f-pr-1 f-pointer-events-none"
              >
                <svg
                  class="f-h-4 f-w-4 f-text-gray-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </span>
            </button>
          </div>
          <div>
            <input
              type="text"
              tabindex="0"
              :id="id"
              :name="name"
              class="f-form-input f-absolute f-left-0 f-py-1 f-w-full f-border-gray-300 focus:f-outline-none f--ml-px f-rounded-md phone-input f-pl-28"
              :class="{
                'f-border-red-300 focus:f-ring-red-500 focus:f-border-red-500 f-text-red-900 f-placeholder-red-300':
                  getFirstError(errors),
              }"
              style="padding-left: 112px !important"
              v-model="value"
              @input="phoneChanged"
              @blur="phoneChanged"
              @change="phoneChanged"
            />
            <div v-show="showOptions">
              <template v-if="loadOptions">
                <div
                  class="f-absolute f-left-0 f-mt-10 f-rounded-md f-border f--ml-px f-w-full f-bg-white"
                >
                  <ul
                    ref="listElement"
                    tabindex="-1"
                    role="listbox"
                    class="f-max-h-56 f-rounded-md f-py-1 f-z-10 f-relative f-h-52 f-bg-white f-text-base f-ring-1 f-ring-black f-ring-opacity-5 f-overflow-auto focus:f-outline-none"
                  >
                    <li
                      v-for="option in options"
                      :key="option.country"
                      tabindex="0"
                      role="option"
                      class="f-text-gray-900 f-cursor-default f-select-none f-relative f-py-2 f-pl-3 f-pr-9 hover:f-bg-gray-200 f-cursor-pointer"
                      :class="{
                        'f-bg-gray-300': phoneCountry === option.country,
                      }"
                      :data-value="option.country"
                      @click="onCallingCodeChanged(option)"
                      @keydown.enter="onCallingCodeChanged(option)"
                      @keydown.space.prevent="onCallingCodeChanged(option)"
                    >
                      <div class="f-flex f-items-center">
                        <img
                          :src="getFlagUrl(option.country)"
                          :alt="option.country"
                          class="f-flex-shrink-0 f-w-5"
                        />
                        <span
                          class="f-ml-3 f-flex-shrink-0 f-w-max f-font-normal"
                        >
                          {{ $t("countries." + option.country) }} +{{
                            option.code
                          }}
                        </span>
                      </div>
                    </li>
                  </ul>
                </div>
              </template>
            </div>
          </div>
        </div>
      </template>
      <template slot="error">
        <div v-if="errors.length" class="f-text-xs f-text-red-600">
          {{ getFirstError(errors) }}
        </div>
      </template>
    </BaseInputGroup>
  </ValidationProvider>
</template>

<script lang="ts">
import { ValidationProvider } from "vee-validate";
import FormResource from "@/module/Form/Resources/FormResource";
import mixins from "vue-typed-mixins";
import DefaultInput from "@/components/Form/Basic/Inputs/DefaultInput.vue";
import FormHelper from "@/module/FormHelper";
import { PropType } from "vue";
import { isStringOrNull } from "@/module/ComponentPropsValidations/validations";
import InputValidationMixin from "@/components/Form/Basic/Inputs/InputValidationMixin";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js/max";
// @ts-ignore
import ClickOutside from "vue-click-outside";
import BaseInputGroup from "@/components/Form/Basic/Inputs/BaseInputGroup.vue";

interface ComponentData {
  options: {
    country: string;
    code: string;
    countryName: string;
  }[];
  callingCode: string | null;
  phoneCountry: string | null;
  showOptions: boolean;
  loadOptions: boolean;

  parsePhoneNumberFromString: (
    text: string,
    defaultCountry?: string | null,
  ) => any;
  getCountries: () => Array<string>;
  getCountryCallingCode: (countryCode: string) => any;
}

export default mixins(DefaultInput, InputValidationMixin).extend({
  name: "BasePhone",

  components: {
    ValidationProvider,
    BaseInputGroup,
  },

  props: {
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
    required: {
      type: Boolean,
      required: true,
    },
    country: {
      required: true,
      validator: isStringOrNull,
    },
    name: {
      type: String,
      required: true,
    },
  },

  data(): ComponentData {
    return {
      options: [],
      callingCode: null,
      phoneCountry: null,
      showOptions: false,
      loadOptions: false,

      parsePhoneNumberFromString: parsePhoneNumberFromString as (
        text: string,
        defaultCountry?: string | null,
      ) => any,
      getCountries: getCountries as () => Array<string>,
      getCountryCallingCode: getCountryCallingCode as (
        countryCode: string,
      ) => any,
    };
  },

  computed: {
    getRules(): object {
      return {
        required: this.required,
        phone: { country: this.phoneCountry },
      };
    },
  },

  methods: {
    triggerLoadPhones(): void {
      this.loadOptions = true;
    },

    closeDropdown() {
      if (this.showOptions) {
        this.showOptions = false;
      }
    },

    triggerShowPhone(): void {
      this.triggerLoadPhones();
      this.showOptions = !this.showOptions;
      const listElement = this.$refs.listElement as HTMLElement | undefined;

      if (listElement) {
        const selected = listElement.querySelector(
          "li[data-value='" + this.phoneCountry + "']",
        ) as HTMLElement | undefined;

        if (selected) {
          this.$nextTick(() => {
            listElement.scroll({
              top: selected.offsetTop,
            });
          });
        }
      }
    },

    onCallingCodeChanged(option: { country: string; code: string }): void {
      this.triggerShowPhone();
      this.callingCodeChanged(option);
    },

    callingCodeChanged(option: { country: string; code: string }): void {
      this.callingCode = option.code;
      this.phoneCountry = option.country;

      this.processValue();
    },

    phoneChanged(): void {
      this.processValue();
    },

    getFlagUrl(countryCode: string): string {
      return (
        this.formResource.baseUrl +
        "/images/flags/" +
        String(countryCode).toUpperCase() +
        ".svg"
      );
    },

    processValue(): void {
      if (
        this.callingCode === null ||
        this.value === null ||
        this.callingCode === "" ||
        this.value === ""
      ) {
        this.update(null);

        return;
      }

      const phone = this.parsePhoneNumberFromString(
        this.value,
        this.phoneCountry,
      );

      if (phone === undefined) {
        return;
      }

      this.update(phone.number as string);

      if (this.callingCode !== phone.countryCallingCode) {
        this.callingCode = phone.countryCallingCode as string;
        this.phoneCountry = phone.country as string;
      }

      this.value = phone.nationalNumber as string;
    },
  },

  watch: {
    country(): void {
      let option = this.options.find((item) => {
        return item.country === this.country;
      });

      if (option === undefined) {
        return;
      }

      if (this.value) {
        const phone = this.parsePhoneNumberFromString(
          this.value,
          this.phoneCountry,
        );

        if (phone !== undefined && phone.isValid()) {
          return;
        }
      }

      this.callingCodeChanged(option);
    },

    showOptions: function (val) {
      const input = document.querySelector(".phone-input") as HTMLElement;
      if (val) {
        input.focus();
      } else {
        input.blur();
      }
    },
  },

  async created(): Promise<void> {
    const countries = this.formResource.countries;
    const allowedPhoneCountries = this.getCountries();
    const defaultCountry =
      this.country ||
      FormHelper.resolveDefaultCountry(
        this.formResource.default_country,
        this.formResource.language,
      );

    for (const index of allowedPhoneCountries) {
      if (["AC", "BQ", "CW"].includes(index)) {
        continue;
      }

      if (!countries.hasOwnProperty(index)) {
        continue;
      }

      this.options.push({
        country: index,
        code: this.getCountryCallingCode(index) as string,
        countryName: countries[index] as string,
      });
    }

    this.options.sort((a, b) => {
      return a.countryName.localeCompare(
        b.countryName,
        this.$root.$i18n.locale,
      );
    });

    const czOption = this.options.find((item) => item.country === "CZ");
    const skOption = this.options.find((item) => item.country === "SK");

    this.options = [
      czOption,
      skOption,
      ...this.options.filter((item) => item !== czOption && item !== skOption),
    ].filter(Boolean) as {
      country: string;
      code: string;
      countryName: string;
    }[];

    let defaultOption = this.options.find((item) => {
      return item.country === defaultCountry;
    });

    if (defaultOption === undefined) {
      defaultOption = this.options[0];
    }

    this.callingCode = defaultOption.code;
    this.phoneCountry = defaultOption.country;
  },

  async mounted(): Promise<void> {
    if (!this.queryParam) {
      return;
    }

    this.loadDataFromQuery();

    if (this.value !== null) {
      let phone = this.parsePhoneNumberFromString(this.value);

      if (phone === undefined) {
        phone = this.parsePhoneNumberFromString(this.value, "CZ");
      }

      if (phone === undefined) {
        phone = this.parsePhoneNumberFromString(this.value, "SK");
      }

      if (phone !== undefined) {
        this.phoneCountry = phone.country as string;
        this.callingCode = phone.countryCallingCode;
        this.value = String(phone.nationalNumber);
      }
    }
    // @ts-ignore
    if (this.show && this.value && this.callingCode) {
      // @ts-ignore
      this.update("+" + this.callingCode + this.value);
    }
  },

  directives: {
    ClickOutside,
  },

  // mixins: [DefaultInput, InputValidationMixin],
});
</script>
